/**
 * Conversation Transcript Modal
 *
 * Read-only viewer that shows a child's actual chat messages with Luno.
 * Used by the Insights "Conversations" tab (full session transcripts) and the
 * Topics drill-down (the real questions behind a topic). Styling mirrors the
 * safety-dashboard conversation viewer for consistency.
 */

import { useEffect } from 'react';
import { X } from 'lucide-react';

export interface TranscriptMessage {
  id: string;
  content: string;
  created_at: string;
  role?: string | null;
  safety_level?: string | null;
}

interface ConversationTranscriptModalProps {
  messages: TranscriptMessage[];
  title: string;
  subtitle?: string;
  isLoading?: boolean;
  highlightMessageId?: string;
  emptyLabel?: string;
  onClose: () => void;
}

export function ConversationTranscriptModal({
  messages,
  title,
  subtitle,
  isLoading = false,
  highlightMessageId,
  emptyLabel = 'No messages to show yet.',
  onClose,
}: ConversationTranscriptModalProps) {
  // Scroll a highlighted message into view on open
  useEffect(() => {
    if (highlightMessageId) {
      setTimeout(() => {
        document
          .getElementById(`transcript-msg-${highlightMessageId}`)
          ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 150);
    }
  }, [highlightMessageId]);

  // Close on Escape
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.45)' }}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-2xl flex flex-col"
        style={{ maxHeight: '85vh' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: '#E8E6E1' }}>
          <div>
            <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 16, fontWeight: 600, color: '#2A2926' }}>
              {title}
            </p>
            {subtitle && (
              <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: '#9B978E', marginTop: 2 }}>
                {subtitle}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg transition hover:bg-gray-100"
            style={{ color: '#9B978E' }}
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-6 py-4 flex flex-col gap-3">
          {isLoading ? (
            <p className="text-center py-10" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: '#9B978E' }}>
              Loading…
            </p>
          ) : messages.length === 0 ? (
            <p className="text-center py-10" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: '#9B978E' }}>
              {emptyLabel}
            </p>
          ) : (
            messages.map((msg) => {
              // Treat anything that isn't explicitly the assistant/buddy as the child.
              const isChild = msg.role !== 'buddy' && msg.role !== 'assistant';
              const isFlagged = msg.id === highlightMessageId;
              return (
                <div
                  key={msg.id}
                  id={`transcript-msg-${msg.id}`}
                  className={`flex ${isChild ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className="rounded-2xl px-4 py-3 max-w-[80%] relative"
                    style={{
                      background: isChild ? '#3ECDC6' : '#F5F3EE',
                      color: isChild ? '#FFFFFF' : '#2A2926',
                      border: isFlagged ? '2px solid #E8946A' : 'none',
                      boxShadow: isFlagged ? '0 0 0 3px rgba(232,148,106,0.2)' : 'none',
                    }}
                  >
                    {isFlagged && (
                      <span
                        className="absolute -top-2 -right-2 px-1.5 py-0.5 rounded-full"
                        style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 10, background: '#E8946A', color: '#fff', fontWeight: 600 }}
                      >
                        Noted
                      </span>
                    )}
                    <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                      {msg.content}
                    </p>
                    <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, marginTop: 4, opacity: 0.6 }}>
                      {new Date(msg.created_at).toLocaleString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit',
                      })}
                    </p>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t text-center" style={{ borderColor: '#E8E6E1' }}>
          <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 12, color: '#9B978E' }}>
            You're viewing your child's chat — this is read-only
          </p>
        </div>
      </div>
    </div>
  );
}
